import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import StorageIcon from '@mui/icons-material/Storage'
import ReplayIcon from '@mui/icons-material/Replay'
import { apiClient } from '../api/client'
import {
  AggregatedSystemHealth,
  ServiceHealthEntry,
  systemHealthService,
} from '../api/adminServices'
import { DLQ_RETRY_ENDPOINTS, retryAllDeadLetterQueuesSequential } from '../api/cronJobService'
import { useToast } from '../components/useToast'

const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  'api-gateway': 'API Gateway',
  'auth-service': 'Auth Service',
  'chat-service': 'Chat Service',
  'dispute-service': 'Dispute Service',
  'driver-service': 'Driver Service',
  'finance-service': 'Finance Service',
  'kyc-service': 'KYC Service',
  'location-service': 'Location Service',
  'notification-service': 'Notification Service',
  'scoring-service': 'Scoring Service',
  'shipment-service': 'Shipment Service',
  'subscription-service': 'Subscription Service',
  'user_management-service': 'User Management',
  'vehicle-service': 'Vehicle Service',
}

// Map service key → DLQ-retry endpoint when one exists. Drives the inline "Retry DLQ" button per row.
const DLQ_BY_SERVICE: Record<string, string> = DLQ_RETRY_ENDPOINTS.reduce(
  (acc, path) => {
    // /api/v1/admin/{service}/dlq/retry
    const m = path.match(/^\/api\/v1\/admin\/([^/]+)\/dlq\/retry$/)
    if (!m) return acc
    const slug = m[1]
    // map slug → backend service key
    const slugToService: Record<string, string> = {
      shipments: 'shipment-service',
      notifications: 'notification-service',
      location: 'location-service',
      vehicles: 'vehicle-service',
      subscriptions: 'subscription-service',
      scores: 'scoring-service',
      chat: 'chat-service',
      kyc: 'kyc-service',
      drivers: 'driver-service',
    }
    const service = slugToService[slug]
    if (service) acc[service] = path
    return acc
  },
  {} as Record<string, string>
)

function statusIcon(status?: string, size: 'small' | 'medium' = 'small') {
  const fontSize = size === 'small' ? 20 : 32
  switch ((status || '').toUpperCase()) {
    case 'UP':
      return <CheckCircleIcon sx={{ fontSize, color: 'success.main' }} />
    case 'DEGRADED':
    case 'OUT_OF_SERVICE':
      return <WarningIcon sx={{ fontSize, color: 'warning.main' }} />
    case 'DOWN':
    case 'UNREACHABLE':
      return <ErrorIcon sx={{ fontSize, color: 'error.main' }} />
    default:
      return <HelpOutlineIcon sx={{ fontSize, color: 'text.disabled' }} />
  }
}

function statusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch ((status || '').toUpperCase()) {
    case 'UP':
      return 'success'
    case 'DEGRADED':
    case 'OUT_OF_SERVICE':
      return 'warning'
    case 'DOWN':
    case 'UNREACHABLE':
      return 'error'
    default:
      return 'default'
  }
}

function statusBg(status: string | undefined, theme: any): string {
  switch ((status || '').toUpperCase()) {
    case 'UP':
      return alpha(theme.palette.success.main, 0.08)
    case 'DEGRADED':
    case 'OUT_OF_SERVICE':
      return alpha(theme.palette.warning.main, 0.1)
    case 'DOWN':
    case 'UNREACHABLE':
      return alpha(theme.palette.error.main, 0.1)
    default:
      return alpha(theme.palette.action.disabled, 0.1)
  }
}

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  jobs: 'Jobs',
  retention: 'Retention',
  matching: 'Matching Engine',
  'batch-joboffers': 'Batch Job-Offers',
}

function SubComponentTile({
  serviceKey,
  entry,
}: {
  serviceKey: string
  entry: ServiceHealthEntry
}) {
  const theme = useTheme()
  const status = entry.status
  const label = entry.role ? ROLE_DISPLAY_NAMES[entry.role] || entry.role : serviceKey
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {serviceKey}
          </Typography>
          <Typography variant="caption" component="div">
            {status}
            {entry.latencyMs != null ? ` · ${entry.latencyMs} ms` : ''}
          </Typography>
          {entry.error && (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {entry.error}
            </Typography>
          )}
        </Box>
      }
    >
      <Paper
        variant="outlined"
        sx={{
          px: 1,
          py: 0.75,
          bgcolor: statusBg(status, theme),
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        {statusIcon(status)}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.1 }}>
            {label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}
            noWrap
          >
            {entry.latencyMs != null ? `${entry.latencyMs} ms` : '—'}
          </Typography>
        </Box>
      </Paper>
    </Tooltip>
  )
}

function ServiceCard({
  serviceKey,
  entry,
  subComponents,
  onRetryDlq,
  isRetrying,
}: {
  serviceKey: string
  entry: ServiceHealthEntry
  subComponents?: [string, ServiceHealthEntry][]
  onRetryDlq?: (service: string, path: string) => void
  isRetrying?: boolean
}) {
  const theme = useTheme()
  const status = entry.status
  const dlqPath = DLQ_BY_SERVICE[serviceKey]

  const displayName = SERVICE_DISPLAY_NAMES[serviceKey] || serviceKey

  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: statusBg(status, theme),
        border: `1px solid ${alpha(
          status === 'UP'
            ? theme.palette.success.main
            : status === 'DEGRADED'
            ? theme.palette.warning.main
            : theme.palette.error.main,
          0.2
        )}`,
        height: '100%',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        {statusIcon(status)}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {serviceKey}
          </Typography>
        </Box>
        <Chip label={status} color={statusColor(status)} size="small" />
      </Stack>

      <Stack direction="row" spacing={2} mb={1}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Latency
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {entry.latencyMs != null ? `${entry.latencyMs} ms` : '—'}
          </Typography>
        </Box>
        {entry.components && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Components
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {Object.keys(entry.components).length}
            </Typography>
          </Box>
        )}
      </Stack>

      {entry.components && Object.keys(entry.components).length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
          {Object.entries(entry.components).map(([name, c]) => (
            <Tooltip key={name} title={`${name}: ${c?.status || '?'}`}>
              <Chip
                label={name}
                size="small"
                color={statusColor(c?.status)}
                variant={c?.status === 'UP' ? 'outlined' : 'filled'}
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Tooltip>
          ))}
        </Stack>
      )}

      {entry.error && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
          <Typography variant="caption">{entry.error}</Typography>
        </Alert>
      )}

      {subComponents && subComponents.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 600, mb: 0.75 }}
          >
            Sub-components ({subComponents.length})
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 0.75,
            }}
          >
            {subComponents.map(([k, e]) => (
              <SubComponentTile key={k} serviceKey={k} entry={e} />
            ))}
          </Box>
        </Box>
      )}

      {dlqPath && (
        <Stack direction="row" justifyContent="flex-end" mt={1}>
          <Button
            size="small"
            startIcon={<ReplayIcon />}
            variant="outlined"
            disabled={!!isRetrying}
            onClick={() => onRetryDlq?.(serviceKey, dlqPath)}
          >
            {isRetrying ? 'Retrying…' : 'Retry DLQ'}
          </Button>
        </Stack>
      )}
    </Paper>
  )
}

export default function SystemHealthPage() {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [retryingService, setRetryingService] = useState<string | null>(null)
  const toast = useToast()
  const flash = (message: string, severity: 'success' | 'error' = 'success') =>
    severity === 'error' ? toast.error(message) : toast.success(message)

  // Try the aggregated admin endpoint first; fall back to the gateway actuator if it 404s
  // (e.g. running against an older backend).
  const {
    data: aggregated,
    isLoading,
    isError,
    refetch,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['system-health-page-aggregated'],
    queryFn: async () => {
      const data = await systemHealthService.getAggregatedHealth()
      return data
    },
    refetchInterval: 30000,
    retry: 1,
  })

  const fallback = useQuery({
    queryKey: ['system-health-page-fallback'],
    queryFn: async () => {
      const data = await systemHealthService.getGatewayHealth()
      return data
    },
    enabled: !!isError,
    retry: 1,
  })

  const retryDlq = async (service: string, path: string) => {
    setRetryingService(service)
    try {
      await apiClient.post(path)
      flash(`DLQ retry triggered for ${SERVICE_DISPLAY_NAMES[service] || service}`)
    } catch (e: any) {
      flash(e?.response?.data?.message || `DLQ retry failed for ${service}`, 'error')
    } finally {
      setRetryingService(null)
    }
  }

  const retryAllMutation = useMutation({
    mutationFn: () => retryAllDeadLetterQueuesSequential(),
    onSuccess: (data) => flash(data.message),
    onError: (e: any) =>
      flash(e?.response?.data?.message || 'Bulk DLQ retry failed', 'error'),
  })

  // Synthesise an aggregated-shape response from the fallback gateway-only health when needed.
  const data: AggregatedSystemHealth | null = useMemo(() => {
    if (aggregated) return aggregated
    if (fallback.data) {
      return {
        status: fallback.data.status,
        services: { 'api-gateway': { status: fallback.data.status, components: fallback.data.components } },
      }
    }
    return null
  }, [aggregated, fallback.data])

  const services = useMemo(() => {
    if (!data?.services) return []
    return Object.entries(data.services)
  }, [data])

  /** Top-level services with their sub-component pods grouped under each parent. */
  const groupedServices = useMemo(() => {
    const subsByParent = new Map<string, [string, ServiceHealthEntry][]>()
    const parents: [string, ServiceHealthEntry][] = []
    for (const [key, entry] of services) {
      if (entry.parent) {
        const list = subsByParent.get(entry.parent) ?? []
        list.push([key, entry])
        subsByParent.set(entry.parent, list)
      } else {
        parents.push([key, entry])
      }
    }
    return parents.map(([key, entry]) => ({
      key,
      entry,
      subs: subsByParent.get(key) ?? [],
    }))
  }, [services])

  const totals =
    data?.totals ??
    services.reduce(
      (acc, [, e]) => {
        if (e.status === 'UP') acc.up += 1
        else if (e.status === 'DEGRADED') acc.degraded += 1
        else acc.down += 1
        acc.total += 1
        return acc
      },
      { up: 0, down: 0, degraded: 0, total: 0 }
    )

  const overall = data?.status ?? (isError ? 'DOWN' : 'UNKNOWN')

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Retry every DLQ in sequence (admin)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReplayIcon />}
                onClick={() => retryAllMutation.mutate()}
                disabled={retryAllMutation.isPending}
              >
                {retryAllMutation.isPending ? 'Retrying all…' : 'Retry All DLQs'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Refresh now">
            <IconButton
              onClick={() => {
                refetch()
                queryClient.invalidateQueries({ queryKey: ['system-health-page-fallback'] })
              }}
              disabled={isFetching}
            >
              {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Banner */}
      <Card
        sx={{
          mb: 3,
          bgcolor: statusBg(overall, theme),
          border: `2px solid ${alpha(
            overall === 'UP'
              ? theme.palette.success.main
              : overall === 'DEGRADED'
              ? theme.palette.warning.main
              : theme.palette.error.main,
            0.4
          )}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {isLoading ? <CircularProgress size={32} /> : statusIcon(overall, 'medium')}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                {isLoading
                  ? 'Checking system status…'
                  : overall === 'UP'
                  ? 'All Systems Operational'
                  : overall === 'DEGRADED'
                  ? 'Degraded Service'
                  : overall === 'DOWN'
                  ? 'System Issues Detected'
                  : 'System Status Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data?.checkedAt
                  ? `Checked ${new Date(data.checkedAt).toLocaleTimeString()} in ${data.durationMs ?? '—'} ms`
                  : 'Aggregated platform health'}
              </Typography>
            </Box>
            <Stack spacing={0.5} alignItems="flex-end">
              <Chip
                label={overall}
                color={statusColor(overall)}
                sx={{ fontWeight: 700, fontSize: '0.875rem', py: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                {totals.up}/{totals.total} UP · {totals.degraded} degraded · {totals.down} down
              </Typography>
            </Stack>
          </Stack>
          {totals.total > 0 && (
            <LinearProgress
              variant="determinate"
              value={Math.round((totals.up / Math.max(1, totals.total)) * 100)}
              color={totals.down > 0 ? 'error' : totals.degraded > 0 ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 4, mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      {isError && !fallback.data && (
        <Alert severity="error" sx={{ mb: 3 }}>
          The aggregated health endpoint (<code>/api/v1/admin/system/health</code>) is unavailable.
          {(error as any)?.response?.status === 404
            ? ' Make sure the latest gateway is deployed.'
            : ` Error: ${(error as Error)?.message || 'unknown'}`}
        </Alert>
      )}

      {isError && fallback.data && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Aggregated health unavailable — showing gateway-only health from{' '}
          <code>/actuator/health</code>.
        </Alert>
      )}

      {/* Service grid */}
      <Box mb={3}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <HealthAndSafetyIcon color="action" />
          <Typography variant="h6" fontWeight={600}>
            Services ({groupedServices.length}
            {data?.subComponentTotals?.total
              ? ` · ${data.subComponentTotals.total} sub-components`
              : ''}
            )
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          {groupedServices.map(({ key, entry, subs }) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
              <ServiceCard
                serviceKey={key}
                entry={entry}
                subComponents={subs}
                onRetryDlq={retryDlq}
                isRetrying={retryingService === key}
              />
            </Grid>
          ))}
          {groupedServices.length === 0 && !isLoading && (
            <Grid item xs={12}>
              <Alert severity="info">
                No services reported yet. The aggregated endpoint may still be initialising.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Component breakdown (collapsed per service, top-level only) */}
      {groupedServices.some(({ entry }) => entry.components && Object.keys(entry.components).length > 0) && (
        <Box mb={3}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <StorageIcon color="action" />
            <Typography variant="h6" fontWeight={600}>
              Component Breakdown
            </Typography>
          </Stack>
          {groupedServices
            .filter(({ entry }) => entry.components && Object.keys(entry.components).length > 0)
            .map(({ key, entry }) => (
              <Accordion key={key} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1} width="100%">
                    {statusIcon(entry.status)}
                    <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {SERVICE_DISPLAY_NAMES[key] || key}
                    </Typography>
                    <Chip label={entry.status} color={statusColor(entry.status)} size="small" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {Object.entries(entry.components || {}).map(([name, c]) => (
                      <Grid item xs={12} sm={6} md={4} key={name}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: statusBg(c?.status, theme),
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {statusIcon(c?.status)}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {name}
                              </Typography>
                              {c?.details && Object.keys(c.details).length > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {Object.entries(c.details)
                                    .slice(0, 2)
                                    .map(([k, v]) => `${k}: ${String(v)}`)
                                    .join(' · ')}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={c?.status || '?'}
                              color={statusColor(c?.status)}
                              size="small"
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      )}

      {/* Raw payload */}
      {data && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              Raw Health Payload (Debug)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                bgcolor: 'action.hover',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                maxHeight: 400,
              }}
            >
              {JSON.stringify(data, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
}
